import { NodeInsights } from "containers/Interfaces";
import { KGObservationDataProto, KGObservationProto } from "build-ts/analysis_plan";
import { ColumnValuesProto } from "build-ts/data";
import { AnalysisTreeNodeProto } from "build-ts/analysis";
import {
  AnalysisColumnType,
  BenchmarkType,
  ColumnType,
  columnTypeToJSON,
  analysisColumnTypeFromJSON,
  TimeBucket,
  analysisColumnTypeToJSON,
  metadataTypeEnumToJSON
} from "build-ts/enums";
import { getOperatorSymbol } from "utils/analysisUtils";
import { formatToSentenceCase, getBenchmarkLabel, getDateFilterName } from "utils/nameUtils";
import { FormatProto, Header, MetricProto, ObservationColumnProto } from "build-ts/metadata";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import { epochConverterSecondWithTimeZone } from "utils/dateUtils";

export interface Benchmark {
  benchmark_value: number[];
  analysis_value: {
    [analysis_type: string]: {
      values: number[];
      name?: string;
    };
  };
  // store the benchmark column's column_name
  column_name?: string;
}

export interface BenchmarksIdsToNameMap {
  [benchmark_id: string]: string;
}

export interface ActualsMap {
  [actual_id: string]: {
    actual_value: number[];
    material_value?: number[];
    analysis_value?: {
      [analysis_type: string]: {
        values: number[];
        name?: string;
      };
    };
    benchmarks: {
      [benchmark_id: string]: Benchmark;
    };
  }
}

export interface ActualsIdToMetricNameMap {
  [actual_id: string]: {
    name: string;
    type: string;
  }
}

export interface ExpandedColumns {
  actuals: Set<string>;
  benchmarks: Set<string>;
}

interface MapItem {
  observationColumnProto?: ObservationColumnProto;
  metricProto?: MetricProto;
  benchmarkEntityProto?: BenchmarkEntityProto;
  isSelected: boolean;
  isPresent: boolean;
}

export interface SuggestionMap {
  [headerId: string]: MapItem;
}

export interface Suggestions {
  attributes: Header[];
  measures: Header[];
  metricEdges: Header[];
  benchmarks: BenchmarkEntityProto[];
  temporal: Header[];
  metrics: Header[];
}
// Helper function to get format for a metric
export function getMetricFormat(
  actualId: string,
  nodes: AnalysisTreeNodeProto | undefined,
  isMaterial: boolean = false
): FormatProto | undefined {
  if (!nodes?.kg_observation) return undefined;

  let metric = nodes?.kg_observation?.metrics.find(
    (m) => m.header?.id === actualId
  );
  if (isMaterial) {
    metric = nodes?.kg_observation?.metrics.find(
      (m) => m.material?.header?.id === actualId
    );

    return metric?.material?.format;
  }

  if (metric?.format) return metric.format;

  return undefined;
}

// Helper function to format a number based on FormatProto
export function formatNumberWithProto(num: number, format?: FormatProto, isPercentage?: boolean): string {
  const decimals = format?.num_decimals ?? 2;

  if (isPercentage || format?.percentage) {
    return (num * 100).toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }) + "%";
  }

  if (format?.currency_code) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: format.currency_code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  }

  return num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// Main formatting function
export function formatWithProto(
  value: number | string | undefined,
  format?: FormatProto,
  opts?: {
    isMaterial?: boolean;
    isVariancePercentage?: boolean;
    isAccomplishPercentage?: boolean;
    isCompositionPercentage?: boolean;
    isRank?: boolean;
    isPercentageMetricVariance?: boolean;
    isAnalysisColumnType?: boolean;
  }
): string {
  if (value === undefined || value === null || value === "") return "-";

  if (typeof value === "string" && isNaN(Number(value))) return value;

  let num = typeof value === "number" ? value : Number(value);
  if (isNaN(num)) return value?.toString() ?? "-";

  if (opts?.isVariancePercentage || opts?.isAccomplishPercentage || opts?.isCompositionPercentage) {
    let formattedValue = formatNumberWithProto(num, { ...format, currency_code: undefined }, true);
    return num < 0 ? `(${formattedValue})` : formattedValue;
  }

  if (opts?.isPercentageMetricVariance) {
    let formattedValue = formatNumberWithProto(num, { ...format, currency_code: undefined }, true);
    return num < 0 ? `(${formattedValue})pp` : `${formattedValue}pp`;
  }

  if (opts?.isRank) {
    // For composition rank, return as plain integer without any formatting
    return num.toString();
  }

  if (opts?.isAnalysisColumnType) {
    let formattedValue = formatNumberWithProto(num, format);
    return num < 0 ? `(${formattedValue})` : formattedValue;
  }

  return formatNumberWithProto(num, format);
}

// function to map actuals_id to column_name
export function actualsIdToMetricName(node: AnalysisTreeNodeProto): ActualsIdToMetricNameMap {
  const map: ActualsIdToMetricNameMap = {};

  // Check in metrics
  node?.kg_observation_data?.kg_observation?.metrics?.forEach((metric) => {
    if (metric.header?.id) {
      map[metric.header.id] = {
        name: metric.header?.name || "",
        type: metadataTypeEnumToJSON(metric.header?.type!).toLowerCase()
      };
    }
  });
  // check in columns
  node?.kg_observation_data?.kg_observation?.columns?.forEach((col) => {
    if (col.column_id) {
      map[col.column_id] = {
        name: col.kg_column_header?.name || "",
        type: columnTypeToJSON(col?.column_type!).toLowerCase()
      };
    }
  });
  return map;
}

// function to build benchmarks_id to benchmark_name map
export function buildBenchmarksIdsToNameMap(node: AnalysisTreeNodeProto): BenchmarksIdsToNameMap {
  const map: BenchmarksIdsToNameMap = {};

  node?.kg_observation_data?.kg_observation?.benchmarks.forEach((benchmark) => {
    if (benchmark.benchmark_type === BenchmarkType.VERSUS_BENCHMARK) {
      if (benchmark?.versus_filters?.length > 0) {
        map[benchmark.header?.id!] = benchmark.header?.name || "";
      }
      else {
        map[benchmark.header?.id!] = getBenchmarkLabel(
          benchmark?.growth_benchmark?.growth_type!,
          benchmark?.growth_benchmark?.number,
          benchmark?.growth_benchmark?.aggregation_type);
      }
    }
    else if (benchmark.benchmark_type === BenchmarkType.TARGET_BENCHMARK) {
      map[benchmark.header?.id!] = benchmark.header?.name || "";
    }
  });
  return map;
}

// function to parse the data_value_json_array
export function safeParseDataValueJsonArray(val: any) {
  if (Array.isArray(val)) {
    // Convert NaN and Infinity to null for consistent handling
    return val.map(item => {
      if (Number.isNaN(item) || !Number.isFinite(item)) {
        return null;
      }
      return item;
    });
  }

  if (typeof val === "string") {
    try {
      // Replace NaN and Infinity with null before parsing
      const updatedVal = val
        .replace(/\bNaN\b/g, "null")
        .replace(/\bInfinity\b/g, "null")
        .replace(/\b-Infinity\b/g, "null");

      return JSON.parse(updatedVal);
    } catch {
      return [];
    }
  }
  return [];
}

// function to build map of actuals_id to its related benchmarks and analysis_columns
export function buildActualsMap(kgObservationData: KGObservationDataProto): ActualsMap {
  const map: ActualsMap = {};

  if (!kgObservationData) return map;

  // Add actuals
  for (const actual of kgObservationData?.actuals) {
    const id = actual.column_hash?.actuals_id;
    const dataValue = safeParseDataValueJsonArray(actual?.data_value_json_array!);

    if (!id) continue;

    map[id] = {
      actual_value: dataValue,
      benchmarks: {}
    };
  }

  // Add material values
  for (const actual of kgObservationData?.material_columns) {
    const id = actual.column_hash?.actuals_id;
    const dataValue = safeParseDataValueJsonArray(actual?.data_value_json_array!);

    const metricId = kgObservationData?.kg_observation?.metrics.find(m => m.material?.header?.id === id)?.header?.id;

    if (metricId) {
      if (map[metricId]) {
        map[metricId].material_value = dataValue;
      }
    }
  }

  // Add benchmarks
  for (const benchmark of kgObservationData?.benchmark_values) {
    const id = benchmark.column_hash?.actuals_id;
    const benchmarkId = benchmark?.column_hash?.benchmark_id;
    let dataValue;
    if (benchmark.data_value_json_array) {
      dataValue = safeParseDataValueJsonArray(benchmark.data_value_json_array);
    }
    if (!id) continue;

    if (benchmarkId && map[id]) {
      // Prefer the explicit column_name if available, otherwise rendering will take care of the default naming
      const columnName = benchmark?.column_hash?.column_name;
      map[id].benchmarks[benchmarkId] = {
        benchmark_value: dataValue,
        analysis_value: {},
        column_name: columnName
      };
    }
  }

  // Add analysis
  for (const analysis of kgObservationData?.analysis_columns) {
    const id = analysis?.column_hash?.actuals_id;
    const benchmarkId = analysis?.column_hash?.benchmark_id;
    const analysisType = analysisColumnTypeToJSON(analysis?.analysis_column_type!);
    let dataValue;
    if (analysis?.data_value_json_array) {
      dataValue = safeParseDataValueJsonArray(analysis.data_value_json_array);
    }

    if (id && benchmarkId) {
      if (map[id] && map[id]?.benchmarks?.[benchmarkId]) {
        if (!map[id]?.benchmarks[benchmarkId]?.analysis_value) {
          map[id].benchmarks[benchmarkId].analysis_value = {};
        }
        if (analysisType) {
          // Prefer the explicit column_name if available, otherwise rendering will take care of the default naming
          const analysisName = analysis.column_hash?.column_name;
          map[id].benchmarks[benchmarkId].analysis_value[analysisType] = {
            values: dataValue,
            name: analysisName,
          };
        }
      }
    } else if (!benchmarkId && id && analysisType) {
      if (map[id]) {
        const actualEntry = map[id];
        if (!actualEntry.analysis_value) {
          actualEntry.analysis_value = {};
        }
        // Prefer the explicit column_name if available, otherwise rendering will take care of the default naming
        const analysisName = analysis.column_hash?.column_name;
        actualEntry.analysis_value[analysisType] = {
          values: dataValue,
          name: analysisName,
        };
      }
    }
  }
  return map;
}

// function to find filter for metric
export function showFilterForMetric(actualId: string, kgObservation: KGObservationProto): string {
  const metric = kgObservation.metrics.find(m => m.header?.id === actualId);
  if (!metric || !metric.definition?.filters) return "";

  const filtersText = metric.definition.filters.map(filter => {
    const name = filter.logical_column_header?.name || "Unknown";
    let operator = "";
    if (filter.operator) {
      operator = getOperatorSymbol(filter.operator);
    }
    const values = filter.values?.join(", ") || "";
    return `${name} ${operator} ${values}`;
  });

  return filtersText.join("; ");
}

// function to find filter for versus benchmark
export function showFilterForVersusBenchmark(benchmarkId: string, kgObservation: KGObservationProto): string {
  const benchmark = kgObservation.benchmarks.find(b => b.header?.id === benchmarkId);
  if (!benchmark) return "";

  if (benchmark.benchmark_type === BenchmarkType.VERSUS_BENCHMARK) {
    if (benchmark.versus_filters && benchmark.versus_filters.length > 0) {
      const filters = benchmark.versus_filters.map(filter => {
        const fieldName = filter.kg_column_header?.name;
        let operator = "";
        if (filter.operator) {
          operator = getOperatorSymbol(filter.operator);
        }
        let values = filter.values.join(", ");
        if (filter.date_filter) {
          values = getDateFilterName(filter.date_filter);
        }
        return `${fieldName} ${operator} ${values}`;
      });
      return filters.join(", ");
    }

    if (benchmark.growth_benchmark) {
      if (benchmark.growth_benchmark.absolute_date_filter) {
        const filter = getDateFilterName(benchmark.growth_benchmark.absolute_date_filter);
        return filter;
      }
    }
  }
  // for target benchmark, return the filters
  if (benchmark.benchmark_type === BenchmarkType.TARGET_BENCHMARK) {
    const allFilters: string[] = [];

    benchmark.definition_mapping.forEach((mapping, index) => {
      if (mapping.benchmark_definition?.content?.filters) {
        const filters = mapping.benchmark_definition?.content?.filters.map(filter => {
          const fieldName = filter.logical_column_header?.name;
          let operator = "";
          if (filter.operator) {
            operator = getOperatorSymbol(filter.operator);
          }
          let values = filter.values.join(", ");
          if (filter.date_filter) {
            values = getDateFilterName(filter.date_filter);
          }
          let filterLabel = `${fieldName} ${operator} ${values}`;
          return filterLabel;
        });
        allFilters.push(...filters);
      }
    });

    if (allFilters.length > 0) {
      return allFilters.join(", ");
    }
  }
  return "";
}

// Helper function to determine column type for flags
export function isMaterialColumn(actualId: string, nodes: AnalysisTreeNodeProto | undefined): boolean {
  if (!nodes?.kg_observation) return false;
  if (nodes?.kg_observation.metrics.find(m => m.material?.header?.id === actualId)) {
    return true;
  }
  return false;
}

// Helper function to find material ID for a metric
export function getMaterialIdForMetric(metricId: string, nodes: AnalysisTreeNodeProto | undefined): string | undefined {
  if (!nodes?.kg_observation) return undefined;
  const metric = nodes.kg_observation.metrics.find(m => m.header?.id === metricId);
  return metric?.material?.header?.id;
}

const getQuarterFromMonth = (date: string, quarterStartMonth: string): string => {
  const monthNames = [
    "jan", "feb", "mar", "apr",
    "may", "jun", "jul", "aug",
    "sep", "oct", "nov", "dec"
  ];

  // Split "MMM-YYYY"
  const parts = date.split("-");
  const month = parts[0].trim().toLowerCase();
  const year = parts[1].trim();

  // find the month index
  const monthNum = monthNames.indexOf(month) + 1;

  // Parse the year
  const yearNum = parseInt(year);

  // Find the quarter-start month index
  const qStart = quarterStartMonth.trim().toLowerCase().slice(0, 3);
  const qStartMonthNum = monthNames.indexOf(qStart) + 1;

  // Determine fiscal year:
  // If month is before the quarter-start, we belong to the *previous* fiscal year
  let fiscalYear = yearNum;
  if (monthNum < qStartMonthNum) {
    fiscalYear -= 1;
  }

  // Compute how many months we are into the fiscal year (0-11)
  const offsetMonths = (monthNum - qStartMonthNum + 12) % 12;

  // Quarter number is floor(offsetMonths/3) + 1
  const quarterNum = Math.floor(offsetMonths / 3) + 1;

  return `Q${quarterNum} ${fiscalYear}`;
};

export function convertTemporalValues(
  timeBucket: TimeBucket,
  data_value_json_array: string,
  defaultTimeZone: string,
  quarterStartMonth: string) {
  if (timeBucket === TimeBucket.DAILY) {
    const epochValues = JSON.parse(data_value_json_array);
    const formattedDates = epochValues.map((epoch: number) => {
      return epochConverterSecondWithTimeZone(epoch, "dd-MMM-yyyy", defaultTimeZone);
    });
    return formattedDates;
  }
  else if (timeBucket === TimeBucket.WEEKLY) {
    const epochValues = JSON.parse(data_value_json_array);
    const formattedDates = epochValues.map((epoch: number) => {
      const startDate = epochConverterSecondWithTimeZone(epoch, "dd-MMM-yyyy", defaultTimeZone);
      const endDate = epochConverterSecondWithTimeZone(epoch + 518400, "dd-MMM-yyyy", defaultTimeZone);   // 518400 is 6 days in seconds
      return `${startDate} - ${endDate}`;
    });
    return formattedDates;
  }
  else if (timeBucket === TimeBucket.MONTHLY) {
    const epochValues = JSON.parse(data_value_json_array);
    const formattedDates = epochValues.map((epoch: number) => {
      return epochConverterSecondWithTimeZone(epoch, "MMM-yyyy", defaultTimeZone);
    });
    return formattedDates;

  }
  else if (timeBucket === TimeBucket.QUARTERLY) {
    const epochValues = JSON.parse(data_value_json_array);

    const formattedDates = epochValues.map((epoch: number) => {
      const date = epochConverterSecondWithTimeZone(epoch, "MMM-yyyy", defaultTimeZone);

      const quarter = getQuarterFromMonth(date, quarterStartMonth);

      return quarter;
    });

    return formattedDates;
  }
  else if (timeBucket === TimeBucket.YEARLY) {
    const epochValues = JSON.parse(data_value_json_array);
    const formattedDates = epochValues.map((epoch: number) => {
      const year = epochConverterSecondWithTimeZone(epoch, "yyyy", defaultTimeZone);
      return year;
    });
    return formattedDates;
  }
  else {
    const epochValues = JSON.parse(data_value_json_array);
    const formattedDates = epochValues.map((epoch: number) => {
      return epochConverterSecondWithTimeZone(epoch, "dd-MMM-yyyy", defaultTimeZone);
    });
    return formattedDates;
  }
};

// Function to get non-measure columns and values
export function getNonMeasureData(node: NodeInsights, defaultTimeZone: string, quarterStartMonth: string) {
  const kgObservation = node?.current_node?.kg_observation;
  const kgObservationData = node?.current_node?.kg_observation_data;
  const nonMeasureColumns = kgObservation?.columns
    .filter(col => col?.column_type === ColumnType.ATTRIBUTE || col?.column_type === ColumnType.TEMPORAL)
    .map(col => ({
      name: col.kg_column_header?.name,
      id: col.column_id,
      isTemporal: col?.column_type === ColumnType.TEMPORAL,
      timeBucket: col?.time_bucket
    }))
    .filter((col): col is
      { name: string; id: string; isTemporal: boolean, timeBucket: TimeBucket } =>
      col.name !== undefined && col.id !== undefined && col.timeBucket !== undefined) || [];


  const nonMeasureValues = nonMeasureColumns.map(col => {
    const value = kgObservationData?.non_measure_values.find(val => val.column_id === col.id);

    // If it's a temporal column, convert epoch seconds to formatted dates
    if (col.isTemporal && value?.data_value_json_array) {
      const formattedDates = convertTemporalValues(
        col.timeBucket,
        value.data_value_json_array,
        defaultTimeZone,
        quarterStartMonth
      );
      return {
        ...value,
        data_value_json_array: JSON.stringify(formattedDates)
      };
    }
    return value;
  }).filter((val): val is ColumnValuesProto => val !== undefined);

  return { nonMeasureColumns, nonMeasureValues };
}

// Converts analysis type string to a properly formatted display text
export function getAnalysisColumnDisplayName(analysisType: string): string {
  // Fall back to standard names if no column_name is available
  const type = analysisColumnTypeFromJSON(analysisType);
  switch (type) {
    case AnalysisColumnType.VARIANCE:
      return "Variance";
    case AnalysisColumnType.VARIANCE_PERCENTAGE:
      return "Variance %";
    case AnalysisColumnType.VARIANCE_CONTRIBUTION:
      return "Contribution";
    case AnalysisColumnType.ACCOMPLISH_PERCENTAGE:
      return "Accomplish %";
    case AnalysisColumnType.COMPOSITION_PERCENTAGE:
      return "Composition %";
    case AnalysisColumnType.RANK:
      return "Rank";
    case AnalysisColumnType.COMPOSITION:
      return "Composition";
    case AnalysisColumnType.MELTED_VARIABLE:
      return "Variable";
    case AnalysisColumnType.MELTED_VALUE:
      return "Value";
    case AnalysisColumnType.LAST_N_PERIOD_AVERAGE:
      return "4-Period Average";
    case AnalysisColumnType.DIAGNOSTIC_STATUS:
      return "Status";
    case AnalysisColumnType.MELTED_BENCHMARK:
      return "Benchmark";
    case AnalysisColumnType.NORMALISED_TARGET:
      return "Normalized Target";
    case AnalysisColumnType.NOT_ANALYSIS_COLUMN:
      return "Not Analysis";
    default:
      // Convert from snake_case to Title Case
      return analysisType
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
  }
}

// function to check if the column is missing
export function isColumnMissing(node: NodeInsights, kgObservationData: KGObservationDataProto): string {
  const missingColumns = kgObservationData.missing_columns;
  if (!missingColumns || missingColumns.length === 0) return "";

  const benchmarkColumnsName = buildBenchmarksIdsToNameMap(node.current_node);
  const actualColumnsName = actualsIdToMetricName(node.current_node);

  let missingMessage = "";

  missingColumns.forEach((column) => {
    const columnName = actualColumnsName[column.actuals_id!]?.name || column.actuals_id;

    if (column.benchmark_id && !column.analysis_column_type) {
      const benchmarkName = benchmarkColumnsName[column.benchmark_id] || column.benchmark_id;
      missingMessage += `Missing benchmark '${benchmarkName}' for column '${columnName}'\n`;
    } else if (column.benchmark_id && column.analysis_column_type) {
      let benchmarkName = benchmarkColumnsName[column.benchmark_id!];
      const analysisType = formatToSentenceCase(analysisColumnTypeToJSON(column.analysis_column_type));
      missingMessage += `Missing '${analysisType}' for ${benchmarkName} for column '${columnName}'\n`;
    } else {
      missingMessage += `Missing column '${columnName}'\n`;
    }
  });

  return missingMessage.trim();
}

// function to get error message from node results
export function getErrorMsg(nodeId: string, nodeInfoMap: Map<string, NodeInsights>): string {
  const node = nodeInfoMap.get(nodeId);
  if (!node) return "";

  const errMsg = node.current_node.node_results?.error_details.map(err => {
    return err.error_msg;
  }).join("\n");

  return errMsg || "";
}

// Function to extract filter strings
export function extractFilterStrings(kgObservation: KGObservationProto, expressionFilters?: string[]): string[] {
  const filterStrings: string[] = [];

  // Process regular filters from kg_observation
  const filters = kgObservation?.filters || [];
  filters.forEach((filter: any) => {
    const columnName = filter?.kg_column_header?.name || "Unknown";
    const operator = getOperatorSymbol(filter?.operator!);
    const values = filter?.values?.length > 0
      ? filter.values.map((value: any) => value.toString().trim()).join(", ")
      : filter?.date_filter?.absolute_date_filter
        ? getDateFilterName(filter?.date_filter?.absolute_date_filter!)
        : filter?.date_filter
          ? getDateFilterName(filter?.date_filter!)
          : "";

    filterStrings.push(`${columnName} ${operator} ${values}`);
  });

  // Add expression filters
  if (expressionFilters) {
    expressionFilters.forEach((exprFilter) => {
      filterStrings.push(exprFilter);
    });
  }

  return filterStrings;
}

// Helper function to extract raw data from node
export function extractRawDataFromNode(node: NodeInsights, defaultTimeZone: string, quarterStartMonth: string): string[][] {
  const kgObservation = node?.current_node?.kg_observation_data?.kg_observation || node?.current_node?.kg_observation;
  const kgObservationData = node?.current_node?.kg_observation_data;

  if (!kgObservation || !kgObservationData) return [];

  // Extract filters if available
  const filterStrings = extractFilterStrings(kgObservation);

  // Get non-measure columns and their raw values
  const { nonMeasureColumns, nonMeasureValues } = getNonMeasureData(node, defaultTimeZone, quarterStartMonth);

  // Build maps
  const actualsMap = buildActualsMap(kgObservationData);
  const benchmarksIdNameMap = buildBenchmarksIdsToNameMap(node.current_node);
  const actualsIdNameMap = actualsIdToMetricName(node.current_node);

  // Determine if it's melted or regular table based on whether we have non-measure columns
  const isMelted = nonMeasureColumns.length === 0;

  if (isMelted) {
    // Melted table format
    const rows: string[][] = [];

    // Add filters at the top if available
    if (filterStrings.length > 0) {
      rows.push(["FILTERS"]);
      filterStrings.forEach((filterStr) => {
        rows.push([filterStr]);
      });
      rows.push([]); // Empty row separator
    }

    // Header row
    const headers = ["Metric", "Actual Value"];

    // Check if we have materials
    const hasMaterials = Object.values(actualsMap).some(data => data?.material_value && data.material_value.length > 0);
    if (hasMaterials) {
      headers.push("Materiality");
    }

    // Check if we have analysis columns without benchmarks
    const hasGlobalAnalysis = Object.values(actualsMap).some(data => data?.analysis_value
      && Object.keys(data?.analysis_value).length > 0);
    if (hasGlobalAnalysis) {
      const analysisTypes = new Set<string>();
      Object.values(actualsMap).forEach(data => {
        if (data?.analysis_value) {
          Object.keys(data?.analysis_value).forEach(type => analysisTypes.add(type));
        }
      });
      analysisTypes.forEach(type => headers.push(getAnalysisColumnDisplayName(type)));
    }

    // Add benchmark columns
    const benchmarkIds = new Set<string>();
    Object.values(actualsMap).forEach(data => {
      Object.keys(data?.benchmarks).forEach(bid => benchmarkIds.add(bid));
    });

    benchmarkIds.forEach(bid => {
      headers.push(benchmarksIdNameMap[bid] || bid);
      // Add analysis columns for each benchmark if any exist
      const hasAnalysis = Object.values(actualsMap).some(data =>
        data?.benchmarks[bid]?.analysis_value && Object.keys(data?.benchmarks[bid].analysis_value).length > 0
      );
      if (hasAnalysis) {
        const analysisTypes = new Set<string>();
        Object.values(actualsMap).forEach(data => {
          if (data?.benchmarks[bid]?.analysis_value) {
            Object.keys(data.benchmarks[bid].analysis_value).forEach(type => analysisTypes.add(type));
          }
        });
        analysisTypes.forEach(type => headers.push(getAnalysisColumnDisplayName(type)));
      }
    });

    rows.push(headers);

    // Data rows
    Object.entries(actualsMap).forEach(([actualId, actualData]) => {
      // Skip if it's only a material column
      const isOnlyMaterial = kgObservation.metrics.some(m => m.material?.header?.id === actualId) &&
        !kgObservation.metrics.some(m => m.header?.id === actualId);
      if (isOnlyMaterial) return;

      const row: string[] = [];

      // Metric name
      row.push(actualsIdNameMap[actualId]?.name || actualId);

      // Actual value (raw number)
      row.push(actualData.actual_value?.[0]?.toString() || "");

      // Material value if applicable
      if (hasMaterials) {
        row.push(actualData.material_value?.[0]?.toString() || "");
      }

      // Global analysis values
      if (hasGlobalAnalysis) {
        const analysisTypes = new Set<string>();
        Object.values(actualsMap).forEach(data => {
          if (data?.analysis_value) {
            Object.keys(data.analysis_value).forEach(type => analysisTypes.add(type));
          }
        });
        analysisTypes.forEach(type => {
          row.push(actualData.analysis_value?.[type]?.values?.[0]?.toString() || "");
        });
      }

      // Benchmark values and their analysis
      benchmarkIds.forEach(bid => {
        row.push(actualData.benchmarks[bid]?.benchmark_value?.[0]?.toString() || "");

        // Analysis for this benchmark
        const hasAnalysis = Object.values(actualsMap).some(data =>
          data.benchmarks[bid]?.analysis_value && Object.keys(data.benchmarks[bid].analysis_value).length > 0
        );
        if (hasAnalysis) {
          const analysisTypes = new Set<string>();
          Object.values(actualsMap).forEach(data => {
            if (data.benchmarks[bid]?.analysis_value) {
              Object.keys(data.benchmarks[bid].analysis_value).forEach(type => analysisTypes.add(type));
            }
          });
          analysisTypes.forEach(type => {
            row.push(actualData.benchmarks[bid]?.analysis_value?.[type]?.values?.[0]?.toString() || "");
          });
        }
      });

      rows.push(row);
    });

    return rows;

  } else {
    // Regular table format
    const rows: string[][] = [];

    // Add filters at the top if available
    if (filterStrings.length > 0) {
      rows.push(["FILTERS"]);
      filterStrings.forEach((filterStr) => {
        rows.push([filterStr]);
      });
      rows.push([]); // Empty row separator
    }

    // Build header row
    const headers: string[] = [];

    // Add non-measure columns
    nonMeasureColumns.forEach(col => headers.push(col.name));

    // Add actual columns
    Object.keys(actualsMap).forEach(actualId => {
      const metricName = actualsIdNameMap[actualId]?.name || actualId;
      headers.push(metricName);

      // Add material if exists
      const hasMaterial = actualsMap[actualId].material_value;
      if (hasMaterial) {
        const metric = kgObservation.metrics.find(m => m.header?.id === actualId);
        const materialName = metric?.material?.header?.name || "Material";
        headers.push(materialName);
      }

      // Add global analysis columns (analysis of actual) - BEFORE benchmarks
      const actualAnalysis = actualsMap[actualId].analysis_value;
      if (actualAnalysis) {
        Object.keys(actualAnalysis).forEach(analysisType => {
          headers.push(getAnalysisColumnDisplayName(analysisType));
        });
      }

      // Add benchmarks
      Object.keys(actualsMap[actualId].benchmarks).forEach(benchmarkId => {
        const benchmarkName = benchmarksIdNameMap[benchmarkId] || benchmarkId;
        headers.push(benchmarkName);

        // Add analysis columns for this benchmark
        const analysisValue = actualsMap[actualId].benchmarks[benchmarkId].analysis_value;
        if (analysisValue) {
          Object.keys(analysisValue).forEach(analysisType => {
            headers.push(getAnalysisColumnDisplayName(analysisType));
          });
        }
      });
    });

    rows.push(headers);

    // Determine number of rows
    const numRows = nonMeasureValues[0]?.data_value_json_array
      ? safeParseDataValueJsonArray(nonMeasureValues[0].data_value_json_array).length
      : 1;

    // Build data rows
    for (let rowIdx = 0; rowIdx < numRows; rowIdx++) {
      const row: string[] = [];

      // Add non-measure values
      nonMeasureValues.forEach(val => {
        const values = safeParseDataValueJsonArray(val.data_value_json_array!);
        row.push(values[rowIdx]?.toString() || "");
      });

      // Add actual and benchmark values
      Object.entries(actualsMap).forEach(([actualId, actualData]) => {
        // Actual value
        row.push(actualData.actual_value?.[rowIdx]?.toString() || "");

        // Material value if exists
        if (actualData.material_value) {
          row.push(actualData.material_value?.[rowIdx]?.toString() || "");
        }

        // Global analysis values (analysis of actual) - BEFORE benchmarks
        if (actualData.analysis_value) {
          Object.values(actualData.analysis_value).forEach(analysis => {
            row.push(analysis.values?.[rowIdx]?.toString() || "");
          });
        }

        // Benchmark values
        Object.entries(actualData.benchmarks).forEach(([benchmarkId, benchmarkData]) => {
          row.push(benchmarkData.benchmark_value?.[rowIdx]?.toString() || "");

          // Analysis values for this benchmark
          if (benchmarkData.analysis_value) {
            Object.values(benchmarkData.analysis_value).forEach(analysis => {
              row.push(analysis.values?.[rowIdx]?.toString() || "");
            });
          }
        });
      });

      rows.push(row);
    }

    return rows;
  }
}
